import { Fragment, memo, useCallback, useMemo, useState, useEffect, useRef } from "react";
import { cloneDeep, isEmpty, isEqual } from "lodash";
import { format } from "date-fns";
import i18next, { t } from "i18next";

import { Box, Select, MenuItem, IconButton, TextField, InputLabel, FormControl, ListSubheader, Autocomplete, Tooltip } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import LockIcon from "@mui/icons-material/Lock";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import DoneAllIcon from '@mui/icons-material/DoneAll';

import codeUtil from "@plannel/pages/utils/code-util";
import colorUtil from "@plannel/pages/utils/color-util";
import reduxUtil from "@plannel/utils/redux-util";
import Snackbar from "@plannel/components/Snackbar"
import { FONT_FAMILY_CSS, FS } from "@plannel/utils/font-util";
import {
  PANEL,
  PANEL_LABEL_SX,
  PanelDangerButton,
  PanelDashedButton,
  PanelHeader,
  PanelPrimaryButton,
} from "@plannel/components/ui/SidePanel";

/*
 * 조건 행의 입력 필드 — 시안의 34px / radius 9 / 옅은 회색 채움.
 * MUI 의 outlined 테두리를 지우고 채움으로 대신한다(라벨 노치가 알약 모양과 싸운다).
 * Select·TextField·DatePicker 가 모두 InputBase 를 쓰므로 한 벌로 통한다.
 */
const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    height: 34,
    borderRadius: "9px",
    bgcolor: PANEL.wellBg,
    fontSize: FS.TITLE,
    color: PANEL.ink,
    "& fieldset": { border: 0 },
    "&:hover fieldset": { border: 0 },
    "&.Mui-focused": { boxShadow: `inset 0 0 0 2px ${PANEL.accentLineHover}` },
    "&.Mui-focused fieldset": { border: 0 },
  },
  "& .MuiSelect-icon": { color: PANEL.inkMuted },
  "& .MuiInputLabel-root": { display: "none" },
  "& .MuiOutlinedInput-notchedOutline legend": { display: "none" },
};

/*
 * 값 입력칸 — 좁은 패널에서 잘리지 않게 flex 로 접히게 둔다.
 *   이전에는 width:100/140 처럼 고정폭이라 패널을 줄이면 값 입력이 잘려 보이지 않았다.
 */
const VALUE_SX = { flex: '1 1 110px', minWidth: 0, ...FIELD_SX };

/* 조건·그룹을 지우는 × — 시안의 회색 아이콘, hover 에서만 빨강 */
const REMOVE_SX = {
  p: 0.25,
  flex: "none",
  color: PANEL.iconFaint,
  "& > svg": { fontSize: 18 },
  "&:hover": { color: PANEL.danger, bgcolor: "transparent" },
};

const tSafe = (key, options) => {
  if (key === null || key === undefined || key === "") {
    return "";
  }
  if (typeof key !== "string") {
    return key;
  }

  // AdvancedFilter has runtime labels; avoid noisy missingKey logs for those values.
  return i18next.exists(key, options) ? t(key, options) : key;
};

const STRING_OPERATORS = [
  "equals",
  "notEqual",
  "contains",
  "notContains",
  "startsWith",
  "endsWith",
  "notConfigured"
];

const NUMBER_OPERATORS = [
  "greaterThan",
  "greaterThanOrEqual",
  "lessThan",
  "lessThanOrEqual",
  "inRange"
];

const BOOLEAN_OPERATORS = [
  "equals"
];

const DATE_OPERATORS = [
  "equals",
  "notEqual",
  "greaterThan",
  "greaterThanOrEqual",
  "lessThan",
  "lessThanOrEqual",
  "inRange"
];

const SELECT_OPERATORS = ["equals"];

/* 모듈 상수라 참조가 고정이다 — 조건 행(memo)의 props 로 넘겨도 다시 그려지지 않는다. */
const getOperatorOptions = (filterType) => {
  if (filterType === "number") { return NUMBER_OPERATORS; }
  if (filterType === "percent") { return NUMBER_OPERATORS; }
  if (filterType === "boolean") { return BOOLEAN_OPERATORS; }
  if (filterType === "select") { return SELECT_OPERATORS; }
  if (filterType === "date" || filterType === "timestamp") { return DATE_OPERATORS; }
  return STRING_OPERATORS;
};

/*
 * headerName 은 부모 경로를 "." 로 이은 값이라, 표시용 이름은 label / rootLabel 로 따로 들고 간다.
 *   headerName 을 "." 로 다시 쪼개면 사용자가 정한 이름(예: 거래처 속성명 "Sales Rep.")의 마침표에서 잘려 빈 이름이 됐다(TC-62).
 */
const extractFilterColumns = (columns, parentHeader = "", rootLabel = null) => {
  const result = [];

  columns.forEach(col => {
    const currentHeader = parentHeader ? `${parentHeader}.${col.headerName}` : col.headerName;

    if (col.children) {
      result.push(...extractFilterColumns(col.children, currentHeader, rootLabel ?? col.headerName));
    } else if (col.filterType) {
      result.push({
        headerName: currentHeader,
        label: col.headerName,
        rootLabel,
        groupLabel: col.groupLabel || null,
        filterType: col.filterType,
        filterName: col.filterName || null,
        filterScope: col.filterScope || null,
        filterOption: col.filterOption || null,
        filterUnit: col.filterUnit || null,
        shouldDisableStartDate: col.shouldDisableStartDate || null,
        shouldDisableEndDate: col.shouldDisableEndDate || null
      });
    }
  });

  return result;
};

const getColumnLabel = (column) => {
  if (column?.label !== undefined) {
    return tSafe(column.label);
  }
  const headerLabel = column?.headerName || "";

  if (headerLabel.includes(".")) {
    const [, second = ""] = headerLabel.split(".");
    return tSafe(second);
  }

  return tSafe(headerLabel);
};

const getColumnGroupLabel = (column) => {
  if (column?.groupLabel) {
    return tSafe(column.groupLabel);
  }
  if (column?.label !== undefined) {
    return tSafe(column.rootLabel);
  }

  const headerLabel = column?.headerName || "";
  if (headerLabel.includes(".")) {
    const [first] = headerLabel.split(".");
    return tSafe(first);
  }

  return "";
};

/*
 * 컬럼 Select 의 메뉴 항목. 조건 행마다 만들면 (행 수 × 컬럼 수)만큼 번역(i18next.exists)과 엘리먼트 생성이 반복된다
 * — 계층 필터를 건 채 열면 조건이 수백 개라 패널이 멈췄다. columnDefs 가 바뀔 때만 한 번 만들어 모든 행이 같은 엘리먼트를 쓴다.
 *   keys : 컬럼 키 집합(행의 `some` 선형 탐색 대신 O(1) 확인)
 */
const buildColumnMenu = (columns) => {
  const items = [];
  const keys = new Set();

  columns.forEach((col, idx) => {
    const key = col.filterName || col.headerName;
    keys.add(key);
    const groupLabel = getColumnGroupLabel(col);
    const prevGroupLabel = idx > 0 ? getColumnGroupLabel(columns[idx - 1]) : "";

    if (groupLabel && groupLabel !== prevGroupLabel) {
      items.push(
        <ListSubheader
          key={`group-${col.headerName}`}
          disableSticky
          sx={{
            p: 1,
            fontWeight: 700,
            fontSize: FS.SUB,
            lineHeight: 1.5,
            minHeight: 'unset',
            color: "primary.main",
            bgcolor: "action.hover",
            borderTop: idx === 0 ? "none" : "1px solid",
            borderColor: "divider"
          }}
        >
          {groupLabel}
        </ListSubheader>
      );
    }

    items.push(
      <MenuItem key={col.headerName} value={key} sx={{ pl: groupLabel ? 2.5 : 1.5 }}>
        {getColumnLabel(col)}
      </MenuItem>
    );
  });

  return { items, keys };
};

/* 요약 줄에 그대로 이어 붙이는 조건 수 상한(넘으면 "… (+N)" 로 줄인다) */
const SUMMARY_MAX_PARTS = 5;

/* 한 그룹에서 처음 그리는 조건 수 — 나머지는 "더 보기" 로 펼친다(수백 개를 한꺼번에 그리면 입력칸 수천 개가 생긴다). */
const FILTER_PAGE_SIZE = 40;

const FilterGroup = memo(({ filterNode, onChange, columnDefs, columnMenu, getOperatorOptions, depth = 0, lockedFilterNames = [], isLocked = false }) => {
  /* 자식 행에 넘기는 콜백을 렌더마다 바꾸지 않으려고 최신 값은 ref 로 읽는다(안 그러면 한 칸 고칠 때 모든 행이 다시 그려진다). */
  const nodeRef = useRef(filterNode);
  nodeRef.current = filterNode;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const [visibleCount, setVisibleCount] = useState(FILTER_PAGE_SIZE);

  const handleChildChange = useCallback((originalIdx, newChild) => {
    const node = nodeRef.current;
    const newChildren = [...node.children];
    newChildren[originalIdx] = newChild;
    onChangeRef.current({ ...node, children: newChildren });
  }, []);

  const handleChildRemove = useCallback((originalIdx) => {
    const node = nodeRef.current;
    onChangeRef.current({ ...node, children: node.children.filter((_, i) => i !== originalIdx) });
  }, []);

  if (filterNode.children) {
    /*
     * 그룹 — 시안의 "AND 척추(spine)" 배치.
     *   왼쪽 세로선 + 논리 연산자 배지 하나가 그룹 전체를 묶고, 조건들은 그 오른쪽 카드에 쌓인다.
     *   이전에는 중첩 Paper 를 depth 만큼 들여쓰기(pl: 1 + depth)해서, 3단만 넘어가도
     *   좌측 여백이 계속 밀려 값 입력칸이 잘렸다. 척추는 깊이가 늘어도 폭을 26px 만 쓴다.
     *   연산자는 Select 대신 배지를 눌러 AND ↔ OR 로 토글한다(시안에 드롭다운이 없다).
     */
    const isOr = filterNode.logicOperator === "OR";
    return (
      <Box sx={{ position: "relative", pl: "26px" }}>
        <Box
          sx={{
            position: "absolute", left: 8, top: 8, bottom: 8,
            width: 2, borderRadius: "1px", bgcolor: "#dbeaf1",
          }}
        />
        <Tooltip title={t("logicOperator")}>
          <Box
            component="button"
            type="button"
            onClick={() => onChange({ ...filterNode, logicOperator: isOr ? "AND" : "OR" })}
            sx={{
              position: "absolute", left: 0, top: "calc(50% - 10px)",
              minWidth: 18, height: 20, px: "4px",
              border: 0, borderRadius: "10px",
              bgcolor: isOr ? PANEL.accentDeep : PANEL.accent,
              color: "#fff",
              font: "inherit", fontSize: FS.CAPTION, fontWeight: 500, lineHeight: "20px",
              letterSpacing: "0.3px", cursor: "pointer",
            }}
          >
            {isOr ? "OR" : "AND"}
          </Box>
        </Tooltip>
        <Box
          sx={{
            borderRadius: "12px",
            p: "12px",
            bgcolor: depth % 2 === 0 ? "#fff" : PANEL.wellBg,
            boxShadow: `inset 0 0 0 1px ${PANEL.lineSoft}`,
            display: "flex",
            flexDirection: "column",
            gap: "9px",
          }}
        >
          {(() => {
            // 조건(leaf)을 먼저, 그룹을 나중에 그린다. 원래 인덱스는 한 번 훑으면서 같이 들고 간다(indexOf 반복 탐색 제거).
            const filters = [];
            const groups = [];
            filterNode.children.forEach((child, idx) => (child.children ? groups : filters).push({ cond: child, originalIdx: idx }));

            // 잠금은 최상위 조건에만, 이름별 첫번째에만 건다.
            const lockedIndexes = new Set();
            if (depth === 0) {
              lockedFilterNames.forEach(name => {
                const firstIdx = filterNode.children.findIndex(c => !c.children && (c.headerName ?? c.filterName) === name);
                if (firstIdx !== -1) {
                  lockedIndexes.add(firstIdx);
                }
              });
            }

            // 조건이 수백 개여도 처음 FILTER_PAGE_SIZE 개만 그린다. 그룹은 개수가 적고 숨기면 안 되므로 항상 그린다.
            const visibleFilters = filters.slice(0, visibleCount);
            const hiddenCount = filters.length - visibleFilters.length;

            return (
              <>
                {[...visibleFilters, ...groups].map(({ cond, originalIdx }, orderedIdx) => {
                  /*
                   * 잠금은 **최상위 조건에만** 건다.
                   *   잠긴 이름(현재는 baseDate)은 화면이 조회 파라미터로 직접 읽어 가는 자리라
                   *   (Workbench 의 `advancedFilter.children.filter(f => f.headerName === "baseDate")`)
                   *   사라지면 조회 기간이 없어진다 — 그래서 × 를 주지 않는다.
                   *   반대로 사용자가 그룹 안에 따로 넣은 base date 는 그냥 조건 하나일 뿐인데,
                   *   예전에는 이름만 보고 잠가 버려서 자기가 넣은 줄을 못 지웠다.
                   */
                  const rowKey = [
                    cond.children ? "group" : "cond",
                    originalIdx,
                    orderedIdx,
                    cond.headerName ?? cond.filterName ?? cond.filterType ?? "unknown"
                  ].join("-");
                  return (
                    <FilterRow
                      key={rowKey}
                      cond={cond}
                      originalIdx={originalIdx}
                      isLocked={lockedIndexes.has(originalIdx)}
                      depth={depth}
                      columnDefs={columnDefs}
                      columnMenu={columnMenu}
                      getOperatorOptions={getOperatorOptions}
                      lockedFilterNames={lockedFilterNames}
                      onChildChange={handleChildChange}
                      onChildRemove={handleChildRemove}
                    />
                  );
                })}
                {hiddenCount > 0 && (
                  <PanelDashedButton
                    pill
                    muted
                    onClick={() => setVisibleCount(count => count + FILTER_PAGE_SIZE)}
                  >
                    {`${t("showMore")} (+${hiddenCount})`}
                  </PanelDashedButton>
                )}
              </>
            );
          })()}

          <Box sx={{ display: "flex", gap: "8px" }}>
            <PanelDashedButton
              pill
              icon={<AddIcon />}
              onClick={() => {
                const defaultField = columnDefs[0];
                // 새로 추가한 조건이 "더 보기" 뒤에 숨지 않도록 보이는 개수를 늘려 둔다.
                setVisibleCount(count => Math.max(count, filterNode.children.length + 1));
                onChange({
                  ...filterNode,
                  children: [...filterNode.children, {
                    filterType: defaultField.filterType,
                    headerName: defaultField.filterName || defaultField.headerName,
                    filterScope: defaultField?.filterScope || null,
                    groupLabel: defaultField?.groupLabel || null,
                    operator: getOperatorOptions(defaultField?.filterType)[0],
                    filterOption: defaultField?.filterOption || null,
                    filterUnit: defaultField?.filterUnit || null,
                    values: defaultField?.filterOption && defaultField?.filterOption.length > 0
                      ? [defaultField?.filterOption[0]] : [""]
                  }]
                });
              }}
            >
              {t("addFilter")}
            </PanelDashedButton>
            <PanelDashedButton
              pill
              muted
              icon={<WorkspacesIcon />}
              onClick={() => {
                onChange({
                  ...filterNode,
                  children: [...filterNode.children, { logicOperator: "AND", children: [] }]
                });
              }}
            >
              {t("addGroup")}
            </PanelDashedButton>
          </Box>
        </Box>
      </Box>
    );
  }

  const renderValueInput = () => {
    if (Array.isArray(filterNode.filterOption) && filterNode.filterOption.length > 0 && filterNode.operator !== "notConfigured") {
      if (!filterNode.values || filterNode.values.length === 0) {
        filterNode.values = [filterNode.filterOption[0]];
      }
      return (
        <FormControl sx={VALUE_SX}>
          <InputLabel id="value-label">{t("value")}</InputLabel>
          <Select
            labelId="value-label"
            size='small'
            variant="outlined"
            value={filterNode.values?.[0]}
            onChange={e => onChange({ ...filterNode, values: [e.target.value] })}
            label="value"
          >
            {filterNode.filterOption.map(option => (
              <MenuItem key={option} value={option}>
                {tSafe(option)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }
    switch (filterNode.filterType) {
      case "boolean":
        if (!filterNode.values || filterNode.values.length === 0 || isEmpty(filterNode.values[0])) {
          filterNode.values = ['Y'];
        }
        return (
          <FormControl sx={VALUE_SX}>
            <InputLabel id="value-label">{t("value")}</InputLabel>
            <Select
              labelId="value-label"
              size='small'
              variant="outlined"
              value={filterNode.values[0]}
              onChange={e => onChange({ ...filterNode, values: [e.target.value] })}
              label="value"
            >
              <MenuItem value={'Y'}>{t("active")}</MenuItem>
              <MenuItem value={'N'}>{t("inactive")}</MenuItem>

            </Select>
          </FormControl>
        );
      case "date":
      case "timestamp": {
        const [start, end] = filterNode.values || [];
        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const unit = filterNode.filterUnit;
        const isQuarter = unit === codeUtil.buckets.QUARTER;
        const isMonth = unit === codeUtil.buckets.MONTH || isQuarter;
        const pickerFormat = isMonth ? 'yyyy-MM' : 'yyyy-MM-dd';
        const pickerViews = isMonth ? ['year', 'month'] : ['year', 'month', 'day'];
        const toStoredValue = date => isMonth ? format(date, 'yyyy-MM-01') : format(date, 'yyyy-MM-dd');
        /*
         * 저장값은 "yyyy-MM-01" 문자열이라 DatePicker 에는 Date 로 바꿔 넘긴다.
         *   문자열을 그대로 넘기면 분기(Q) 버킷에서 값 검증이 shouldDisableMonth 에 문자열을 넘겨 e.getMonth 가 터지고 화면이 하얗게 됐다.
         */
        const monthOf = e => (e instanceof Date ? e : new ZDate(e)).getMonth();
        const disableNonQuarterStartMonth = e => ![0, 3, 6, 9].includes(monthOf(e));
        const disableNonQuarterEndMonth = e => ![2, 5, 8, 11].includes(monthOf(e));
        const rangeStartDate = start ? new ZDate(start) : undefined;
        const rangeEndDate = end ? new ZDate(end) : undefined;
        /*
         * 일 단위(주 버킷) 달력에서 고를 수 있는 날짜는 화면이 컬럼에 넘긴 규칙을 따른다.
         *   DP Workbench 는 상단 기간과 같게 시작일은 주 시작 요일, 종료일은 주 마지막 요일만 열어 둔다.
         */
        const dateColumn = isMonth ? null : columnDefs?.find(col => (col.filterName || col.headerName) === filterNode.headerName);
        const disableStartDate = dateColumn?.shouldDisableStartDate ?? undefined;
        const disableEndDate = dateColumn?.shouldDisableEndDate ?? undefined;
        return filterNode.operator === "inRange" ? (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t("periodStart")}
              format={pickerFormat}
              views={pickerViews}
              value={rangeStartDate ?? null}
              maxDate={rangeEndDate}
              shouldDisableMonth={isQuarter ? disableNonQuarterStartMonth : undefined}
              shouldDisableDate={disableStartDate}
              onChange={date => onChange({
                ...filterNode,
                values: [toStoredValue(date), end],
                timeZone: userTimeZone
              })}
              slotProps={{ textField: { size: "small", sx: VALUE_SX } }}
            />
            <DatePicker
              label={t("periodEnd")}
              format={pickerFormat}
              views={pickerViews}
              value={rangeEndDate ?? null}
              minDate={rangeStartDate}
              shouldDisableMonth={isQuarter ? disableNonQuarterEndMonth : undefined}
              shouldDisableDate={disableEndDate}
              onChange={date => onChange({
                ...filterNode,
                values: [start || "", toStoredValue(date)],
                timeZone: userTimeZone
              })}
              slotProps={{ textField: { size: "small", sx: VALUE_SX } }}
            />
          </LocalizationProvider>
        ) : (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t("value")}
              format={pickerFormat}
              views={pickerViews}
              value={rangeStartDate ?? null}
              shouldDisableMonth={isQuarter ? disableNonQuarterStartMonth : undefined}
              shouldDisableDate={disableStartDate}
              onChange={date => onChange({
                ...filterNode,
                values: [toStoredValue(date)],
                timeZone: userTimeZone
              })}
              slotProps={{ textField: { size: "small", sx: VALUE_SX } }}
            />
          </LocalizationProvider>
        );
      }
      case "percent":
        return filterNode.operator === "inRange" ? (
          <>
            <TextField
              size="small"
              type="number"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', step: 1 }}
              sx={VALUE_SX}
              value={filterNode.values?.[0] !== null && filterNode.values[0] !== "" ? Math.round(filterNode.values[0] * 100) : ""}
              onChange={e => {
                const rawValue = e.target.value;
                onChange({ ...filterNode, values: [rawValue === "" ? "" : Math.round(rawValue) / 100, filterNode.values?.[1] ?? ""] })
              }}
              onKeyDown={e => { if (['.', ','].includes(e.key)) e.preventDefault(); }}
              label={"From"}
            />
            <TextField
              size="small"
              type="number"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', step: 1 }}
              sx={VALUE_SX}
              value={filterNode.values?.[1] !== null && filterNode.values[1] !== "" ? Math.round(filterNode.values[1] * 100) : ""}
              onChange={e => {
                const rawValue = e.target.value;
                onChange({ ...filterNode, values: [filterNode.values?.[0] ?? "", rawValue === "" ? "" : Math.round(rawValue) / 100] })
              }}
              onKeyDown={e => { if (['.', ','].includes(e.key)) e.preventDefault(); }}
              label={"To"}
            />
          </>
        ) : (
          <TextField
            size="small"
            type="number"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', step: 1 }}
            sx={VALUE_SX}
            value={filterNode.values?.[0] !== null && filterNode.values[0] !== "" ? Math.round(filterNode.values[0] * 100) : ""}
            onChange={e => {
              const rawValue = e.target.value;
              onChange({ ...filterNode, values: rawValue === "" ? [] : [Math.round(rawValue) / 100] })
            }}
            onKeyDown={e => { if (['.', ','].includes(e.key)) e.preventDefault(); }}
            label={t("value")}
          />
        )
      case "number":
        return filterNode.operator === "inRange" ? (
          <>
            <TextField
              size="small"
              type="number"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              sx={VALUE_SX}
              value={filterNode.values?.[0] ?? ""}
              onChange={e => {
                const { value } = e.target;
                if (value === "") {
                  onChange({
                    ...filterNode,
                    values: ["", filterNode.values?.[1] ?? ""],
                  });
                  return;
                }
                const newValue = Number(value);
                if (Number.isNaN(newValue)) {
                  return;
                }
                onChange({ ...filterNode, values: [newValue, filterNode.values?.[1] ?? ""] });
              }}
              label={"From"}
            />
            <TextField
              size="small"
              type="number"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              sx={VALUE_SX}
              value={filterNode.values?.[1] ?? ""}
              onChange={e => {
                const { value } = e.target;
                if (value === "") {
                  onChange({ ...filterNode, values: [filterNode.values?.[0] ?? "", ""] });
                  return;
                }
                const newValue = Number(value);
                if (Number.isNaN(newValue)) {
                  return;
                }
                onChange({ ...filterNode, values: [filterNode.values?.[0] ?? "", newValue] }) }}
              label={"To"}
            />
          </>
        ) : (
          <TextField
            size="small"
            type="number"
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
            sx={VALUE_SX}
            value={filterNode.values?.[0] ?? ""}
            onChange={e => {
              const { value } = e.target;
              if (value === "") {
                onChange({ ...filterNode, values: [] });
                return;
              }
              const newValue = Number(value);
              if (Number.isNaN(newValue)) {
                return;
              }
              onChange({ ...filterNode, values: [newValue] });
            }}
            label={t("value")}
          />
        )
      case "string":
        return filterNode.operator === "notConfigured" ? null :
          <TextField
            size="small"
            sx={VALUE_SX}
            value={filterNode.values?.[0] || ""}
            onChange={e => onChange({ ...filterNode, values: [e.target.value] })}
            label={t("value")}
          />
      default:
        return (
          <TextField
            size="small"
            sx={VALUE_SX}
            value={filterNode.values?.[0] || ""}
            onChange={e => onChange({ ...filterNode, values: [e.target.value] })}
            label={t("value")}
          />
        )
    }
  }
  if (!filterNode.operator) {
    filterNode.operator = getOperatorOptions(filterNode.filterType)[0];
  }
  return (
    /*
     * 조건 한 줄 — 컬럼 / 연산자 / 값. 시안처럼 값이 아래로 접히도록 flex-wrap 을 쓴다.
     *   가로 한 줄(Stack)로 두면 패널을 좁혔을 때 값 입력이 잘려 나갔다.
     */
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "5px" }}>
      <FormControl sx={VALUE_SX}>
        <InputLabel id="column-label">{t("column")}</InputLabel>
        <Select
          labelId="column-label"
          label="column"
          value={columnMenu.keys.has(filterNode.headerName)
            ? filterNode.headerName
            : columnDefs[0]?.filterName || columnDefs[0]?.headerName || ''}
           onChange={e => {
             if (isLocked) {
               return;
             }
             const headerName = e.target.value;
             const selectedCol = columnDefs.find(col => (col.filterName || col.headerName) === headerName);
             const filterType = selectedCol?.filterType || "string";
             onChange({
               ...filterNode,
               headerName,
               filterName: selectedCol?.filterName || headerName,
               filterScope: selectedCol?.filterScope || null,
               groupLabel: selectedCol?.groupLabel || null,
               filterType,
               operator: getOperatorOptions(filterType)[0],
               filterOption: selectedCol?.filterOption || null,
               filterUnit: selectedCol?.filterUnit || null,
               values: selectedCol?.filterOption && selectedCol.filterOption.length > 0
                 ? [selectedCol.filterOption[0]] : [""]
             });
           }}
          size="small"
          variant="outlined"
          disabled={isLocked}
        >
          {columnMenu.items}
        </Select>
      </FormControl>
      {filterNode.filterType !== "select" && (
        <FormControl sx={VALUE_SX}>
          <InputLabel id="operator-label">{t("operator")}</InputLabel>
          <Select
            labelId="operator-label"
            label='operator'
            value={filterNode.operator}
            onChange={e => {
              if (isLocked) {
                return;
              }
              onChange({ ...filterNode, operator: e.target.value });
            }}
            size="small"
            variant="outlined"
            sx={{ width: "100%" }}
            disabled={filterNode.filterType === "boolean" || isLocked}
          >
            {getOperatorOptions(filterNode.filterType).map(op => (
              <MenuItem key={op} value={op}>
                {t(op)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {renderValueInput()}
    </Box>
  );
});

/*
 * 그룹 안의 한 줄(조건 또는 하위 그룹) + 지우기 ×.
 * memo 라서 다른 줄을 고쳐도 이 줄은 다시 그려지지 않는다 — props 가 전부 안정적인 참조(콜백은 useCallback, 노드는 바뀐 것만 새 객체)여야 한다.
 */
const FilterRow = memo(({ cond, originalIdx, isLocked, depth, columnDefs, columnMenu, getOperatorOptions, lockedFilterNames, onChildChange, onChildRemove }) => {
  const handleChange = useCallback(newChild => onChildChange(originalIdx, newChild), [onChildChange, originalIdx]);
  const handleRemove = useCallback(() => onChildRemove(originalIdx), [onChildRemove, originalIdx]);

  /*
   * 조건 노드 → 화면에 보이는 컬럼 이름 (잠긴 조건의 툴팁에서만 쓴다).
   * 노드가 들고 있는 것은 바인딩 키(headerName = 컬럼의 filterName)라 그대로 번역하면 키가 나온다
   * — 기간 조건이 "Base Date" 가 아니라 "baseDate" 로 보이던 이유다(컬럼 쪽 라벨 키는 `baseDt`).
   * 조건 행의 컬럼 Select 와 같은 방법으로 columnDefs 에서 찾아 라벨을 얻는다.
   */
  const getConditionLabel = () => {
    const key = cond?.headerName ?? cond?.filterName;
    const column = columnDefs?.find(col => (col.filterName || col.headerName) === key);
    return column ? getColumnLabel(column) : tSafe(key);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: "6px" }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <FilterGroup
          filterNode={cond}
          onChange={handleChange}
          columnDefs={columnDefs}
          columnMenu={columnMenu}
          getOperatorOptions={getOperatorOptions}
          depth={depth + 1}
          lockedFilterNames={lockedFilterNames}
          isLocked={isLocked}
        />
      </Box>
      {isLocked ? (
        /*
         * 잠긴 조건(기간 등)은 지울 수 없다 — 왜 × 가 없는지 자물쇠로 알린다.
         * 툴팁에 이유를 적는다: 예전에는 컬럼 이름만 띄워서, 정작 자물쇠가 무슨 뜻인지
         * 화면에서 알 길이 없었다.
         */
        <Tooltip title={`${getConditionLabel()} · ${t("MSG_RequiredFilterCondition")}`}>
          <LockIcon sx={{ mt: "8px", fontSize: 15, color: PANEL.iconFaint, flex: "none" }} />
        </Tooltip>
      ) : (
        <IconButton size="small" sx={{ ...REMOVE_SX, mt: "5px" }} onClick={handleRemove}>
          <CloseIcon />
        </IconButton>
      )}
    </Box>
  );
});

// --- 2. 메인 AdvancedFilter 컴포넌트 ---
const EMPTY_ADVANCED_FILTER = { logicOperator: "AND", children: [] };
const DEFAULT_ITEM_TYPE = "FG";

/* 헤더 부제에 쓰는 조건 개수 — 그룹은 세지 않고 잎(실제 조건)만 센다. */
const countConditions = node => {
  if (!node) {
    return 0;
  }
  return node.children
    ? node.children.reduce((sum, child) => sum + countConditions(child), 0)
    : 1;
};

/*
 * 조회 레벨 / 품목 유형 드롭다운은 **앱 표준 룩**(MUI outlined + 떠 있는 라벨, size="small")을 그대로 둔다.
 * 시안은 여기도 알약(38px/radius 19)으로 그렸지만, 이 컨트롤들은 다른 화면의 필터 툴바와
 * 같은 얼굴이어야 한다 — index.js 테마도 "닫힌 입력 필드(높이·테두리·폰트)는 건드리지 않음"을
 * 명시적으로 지키고 있고, 팝업 룩만 전역에서 통일한다.
 * 알약은 조건 행 안쪽(FIELD_SX)에만 쓴다.
 */
const LEVEL_SX = { flex: 1, minWidth: 0 };

const normalizeAdvancedFilterTree = (filters) => {
  const tree = filters?.advancedFilter ?? filters?.filter ?? filters;

  if (tree && typeof tree === "object" && Array.isArray(tree.children)) {
    return tree;
  }

  return EMPTY_ADVANCED_FILTER;
};

const getLeafName = node => node?.headerName ?? node?.filterName;

const hasUserConditions = (tree, lockedFilterNames = []) => {
  return (tree?.children ?? []).some(child => child.children || !lockedFilterNames.includes(getLeafName(child)));
};

/*
 * 트리 안의 잠긴 baseDate 노드 값을 화면의 현재 기간(dataMap.startDate/endDate)으로 맞춘다.
 *   baseDate 의 원본은 Workbench 의 filterStartDate/filterEndDate 이고, 트리의 baseDate 노드는 Confirm 시점의 스냅샷일 뿐이다.
 *   Confirm 후 패널을 닫고 상단 날짜를 바꾸면 스냅샷만 옛 값으로 남아, 다시 열 때 모달에 옛 날짜가 되살아났다.
 */
/* Date 를 그대로 트리에 넣으면 Confirm 의 JSON 직렬화에서 UTC ISO 문자열이 되어(KST 05-01 → 04-30T15:00Z) 하루/한 달 밀린다. */
const toDateOnly = value => (value instanceof Date && !isNaN(value) ? format(value, "yyyy-MM-dd") : value);

/*
 * 버킷(filterUnit)도 같이 맞춘다. 노드의 filterUnit 은 생성/Confirm 시점 값이라
 *   월 → 주로 바꿔도 달력이 월 단위로 남고, 주 → 월로 되돌려도 주 단위로 남았다(TC-41).
 */
const syncBaseDate = (tree, dataMap) => {
  const hasRange = Boolean(dataMap?.startDate && dataMap?.endDate);
  const unit = dataMap?.timeDimension;
  if (!hasRange && !unit) {
    return tree;
  }

  return {
    ...tree,
    children: (tree?.children ?? []).map(child =>
      !child.children && getLeafName(child) === "baseDate"
        ? {
          ...child,
          ...(unit ? { filterUnit: unit } : {}),
          ...(hasRange ? { values: [toDateOnly(dataMap.startDate), toDateOnly(dataMap.endDate)] } : {})
        }
        : child
    )
  };
};

const buildInitialFilter = (advancedFilters, dataMap, lockedFilterNames = []) => {
  const normalized = cloneDeep(normalizeAdvancedFilterTree(advancedFilters));

  if (hasUserConditions(normalized, lockedFilterNames)) {
    return syncBaseDate(normalized, dataMap);
  }

  /* 시드에 없는 잠긴 조건은 살려서 앞에 붙인다(잠긴 조건은 지울 수 없는 필수 조건이므로). */
  const lockedChildren = (normalized.children ?? [])
    .filter(child => !child.children && lockedFilterNames.includes(getLeafName(child)));
  const withLocked = seed => {
    const seedNames = new Set((seed.children ?? []).filter(child => !child.children).map(getLeafName));
    const missingLocked = lockedChildren.filter(child => !seedNames.has(getLeafName(child)));
    return { ...seed, children: [...missingLocked, ...(seed.children ?? [])] };
  };

  // 1순위: Workbench에서 변환된 전체 초기 조건
  if (dataMap?.initialConditions?.children?.length) {
    return withLocked(cloneDeep(dataMap.initialConditions));
  }

  // 2순위: 날짜만 있는 경우 (fallback)
  if (dataMap?.startDate && dataMap?.endDate) {
    return withLocked({
      ...normalized,
      children: [{
        filterType: "date",
        headerName: "baseDate",
        filterName: "baseDate",
        filterScope: null,
        groupLabel: null,
        displayName: null,
        operator: "inRange",
        filterUnit: dataMap?.timeDimension || "month",
        values: [toDateOnly(dataMap.startDate), toDateOnly(dataMap.endDate)]
      }]
    });
  }

  return normalized;
};

const AdvancedFilter = ({ columnDefs, advancedFilters, onApply, onRemoveAll = null, dataMap = {}, viewName = "", lockedFilterNames = [], itemTypes = [], timeDimension = null, onClose = null, isFilterOpen = false }) => {
   /*
    * 렌더마다 cloneDeep 하면 view state(그리드 컬럼·필터 등 큰 객체)를 매번 통째로 복사한다 — 읽기만 하므로 한 번만 가져온다.
    * 기본값은 최초 마운트 시점의 값이면 충분하다(아래 useState 초기값과 레벨 초기화에서만 쓴다).
    */
   const reduxViewState = useMemo(() => (viewName ? cloneDeep(reduxUtil.getViewState(viewName)) : null),
     // eslint-disable-next-line react-hooks/exhaustive-deps
     [viewName]);
   const { itemType } = codeUtil;
   const { snackBarMessageColor } = colorUtil;

   const { customer: CUSTOMER, item: ITEM } = codeUtil;
   const { CUST_LEVEL, ITEM_LEVEL } = codeUtil.dpSettingCode;

   const [advancedFilter, setAdvancedFilter] = useState(() => buildInitialFilter(advancedFilters, dataMap, lockedFilterNames));

   /*
    * remove all 로 **사용자가 방금 비운 트리**. 프롭이 이 값으로 되돌아오는 동안은 초기 조건을 다시 심지 않는다.
    *   buildInitialFilter 는 "children 이 비어 있으면 dataMap.initialConditions 를 심는다" 라서
    *   비운 트리(잠긴 조건이 아직 없으면 children 이 아예 빈다)를 올리면
    *   → 부모 프롭 갱신 → 아래 동기화 효과 → 초기 조건 재주입 이 되어,
    *   조회 결과가 올라오기 전에 remove all 을 누르면 아무리 눌러도 조건이 사라지지 않았다.
    */
   const clearedTreeRef = useRef(null);

   /*
    * 계층필터를 적용한 후 Advanced Filter panel을 열 때 초기 조건이 반영되도록 한다.
    * clearedTreeRef를 확인하여 removeAll 직후의 재주입을 방지하면서도,
    * 계층필터 등 다른 필터 변경에는 반응한다.
    *   - removeAll 직후: clearedTreeRef.current가 현재 advancedFilters와 같으면 업데이트 스킵
    *   - Panel이 열려있으면: initialConditions가 null이므로 절대 업데이트 안 함
    *   - Panel이 닫혔다가 열릴 때: initialConditions가 있으면 반영
    */
   /*
    * lockedFilterNames 는 호출부마다 `lockedFilterNames={["baseDate"]}` 처럼 인라인 배열로 넘어와 렌더마다 참조가 바뀐다.
    *   그대로 deps 에 두면 부모가 다시 그려질 때마다(예: 그리드 셀 클릭 → 차트 상태 갱신) 아래 효과가 돌아,
    *   패널에서 아직 Confirm 하지 않은 조건이 적용된 advancedFilters/initialConditions 로 덮어써져 사라진다.
    *   → 값으로 비교하도록 문자열 키를 deps 로 쓴다.
    */
   const lockedFilterNamesKey = (lockedFilterNames ?? []).join("|");

   useEffect(() => {
     // Panel이 열려있고 initialConditions가 null이면 절대 업데이트 금지
     if (isFilterOpen && !dataMap?.initialConditions) {
       return;
     }

     // removeAll 직후이고 아직 상태가 변하지 않았으면 업데이트 스킵
     if (clearedTreeRef.current !== null && isEqual(clearedTreeRef.current, advancedFilters)) {
       return;
     }

     // removeAll 아님 또는 상태가 변했음 → clearedTreeRef 리셋 및 상태 업데이트
     clearedTreeRef.current = null;
     const newFilter = buildInitialFilter(advancedFilters, dataMap, lockedFilterNames);
     setAdvancedFilter(newFilter);
     // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [advancedFilters, dataMap?.initialConditions, lockedFilterNamesKey, isFilterOpen]);

   useEffect(() => {
     if (!timeDimension && !dataMap?.startDate && !dataMap?.endDate) {
       return;
     }

     setAdvancedFilter(prev => syncBaseDate({
       ...prev,
       filterUnit: timeDimension,
       startDate: dataMap?.startDate ?? prev.startDate,
       endDate: dataMap?.endDate ?? prev.endDate
     }, { ...dataMap, timeDimension: timeDimension ?? dataMap?.timeDimension }));
     // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [timeDimension, dataMap?.startDate, dataMap?.endDate]);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [itemLevels, setItemLevels] = useState([]);
  const [custLevels, setCustLevels] = useState([]);
  const [itemLevelCd, setItemLevelCd] = useState(advancedFilters?.itemLevelCd ?? reduxViewState?.itemLevelCd ?? dataMap?.itemLevelCd ?? ITEM);
  const [custLevelCd, setCustLevelCd] = useState(advancedFilters?.custLevelCd ?? reduxViewState?.custLevelCd ?? dataMap?.custLevelCd ?? CUSTOMER);
  const [itemTypeSearch, setItemTypeSearch] = useState(advancedFilters?.itemType ?? reduxViewState?.itemTypeSearch ?? DEFAULT_ITEM_TYPE);
  /*
   * 고급 필터의 계층 그룹 번호는 고급 필터가 레벨을 직접 골랐을 때만 그 필터의 것이다.
   *   레벨 없이 Confirm 한 필터의 번호는 그때 상단 그룹의 스냅샷이라, 상단에서 그룹을 바꾼 뒤 패널을 다시 열면 옛 그룹(예: 2)이 되살아나
   *   새 그룹(1)의 레벨·계층 코드와 함께 나가 0 건이 됐다(TC-57). 그때는 지금 그리드의 그룹(dataMap)을 따른다.
   */
  const [custHrchyNum, setCustHrchyNum] = useState((advancedFilters?.custLevelCd ? advancedFilters?.custHrchyNum : null) ?? reduxViewState?.custHrchyNum ?? dataMap?.custHrchyNum ?? codeUtil.hrchyNum.HRCHY);
  const [itemHrchyNum, setItemHrchyNum] = useState((advancedFilters?.itemLevelCd ? advancedFilters?.itemHrchyNum : null) ?? reduxViewState?.itemHrchyNum ?? dataMap?.itemHrchyNum ?? codeUtil.hrchyNum.HRCHY);

  const getLevelKeys = (level) => {
    if (typeof level === "object") {
      return [level?.id, level?.cd].filter(Boolean);
    }
    return [level].filter(Boolean);
  };

  const findLevelByKey = (targetLevel, levelOptions = []) => {
    const keys = getLevelKeys(targetLevel);
    if (!keys.length) {
      return null;
    }
    return levelOptions.find(level => keys.includes(level?.id) || keys.includes(level?.cd)) ?? null;
  };

  const getResolvedLevel = (selectedLevel, levelOptions, fallbackLevel) => {
    const matchedLevel = findLevelByKey(selectedLevel, levelOptions);
    if (matchedLevel) {
      return matchedLevel;
    }

    const fallbackMatchedLevel = findLevelByKey(fallbackLevel, levelOptions);
    if (fallbackMatchedLevel) {
      return fallbackMatchedLevel;
    }

    return levelOptions[0] ?? null;
  };

  const getSelectValue = (selectedLevel, levelOptions) => {
    const matchedLevel = findLevelByKey(selectedLevel, levelOptions);
    return matchedLevel?.id ?? "";
  };

  // 렌더마다 새 배열이 되면 조건 행(FilterGroup memo)이 매번 다시 그려진다 — columnDefs 가 바뀔 때만 계산한다.
  const filterColumns = useMemo(() => extractFilterColumns(columnDefs), [columnDefs]);
  const columnMenu = useMemo(() => buildColumnMenu(filterColumns), [filterColumns]);

  /* 호출부가 인라인 배열로 넘겨 오므로 값(문자열 키)이 같으면 같은 참조를 쓴다 — 조건 행 memo 가 깨지지 않게. */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const lockedNames = useMemo(() => lockedFilterNames ?? [], [lockedFilterNamesKey]);

  const hasEmptyValue = node => {
    if (node.children?.length) {
      return node.children.some(hasEmptyValue);
    }
    return (isEmpty(node.values) || node.values.some(v => v === "" || v === null)) && node.operator !== "notConfigured";
  };

  const handleConfirm = () => {
    if (hasEmptyValue(advancedFilter) && advancedFilter?.children?.length) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: `${t("value")}` }) });
      return;
    }

    if (!isEmpty(advancedFilter)) {
      const hasNonDefault = itemLevelCd?.cd !== ITEM?.cd || custLevelCd?.cd !== CUSTOMER?.cd || itemTypes.length > 0 || custHrchyNum !== codeUtil.hrchyNum.HRCHY || itemHrchyNum !== codeUtil.hrchyNum.HRCHY;
      if (hasNonDefault) {
        const params = { advancedFilter };
        if (itemLevelCd?.cd !== ITEM?.cd) params.itemLevelCd = itemLevelCd?.cd;
        if (custLevelCd?.cd !== CUSTOMER?.cd) params.custLevelCd = custLevelCd?.cd;
        if (custHrchyNum !== codeUtil.hrchyNum.HRCHY) params.custHrchyNum = custHrchyNum;
        if (itemHrchyNum !== codeUtil.hrchyNum.HRCHY) params.itemHrchyNum = itemHrchyNum;
        if (itemTypes.length > 0) params.itemType = itemTypeSearch;
        onApply?.(JSON.parse(JSON.stringify(params)));
      } else {
        onApply?.(JSON.parse(JSON.stringify(advancedFilter)));
      }
    }
  };

  /*
   * 요약 줄에 쓸 날짜 표기.
   * ⚠ 이미 `yyyy-MM-dd` 로 저장된 값은 **파싱하지 않고 잘라 쓴다** — `new Date("2026-08-27")` 는
   *   UTC 자정이라 로컬 시간대에 따라 하루 앞으로 밀린다(음수 오프셋 지역).
   */
  const formatDateValue = (value, filterUnit) => {
    if (value === null || value === undefined || value === "") {
      return "";
    }
    const text = String(value);
    const isMonth = filterUnit === codeUtil.buckets.MONTH || filterUnit === codeUtil.buckets.QUARTER;
    if (/^\d{4}-\d{2}(-\d{2})?/.test(text)) {
      return text.slice(0, isMonth ? 7 : 10);
    }
    const date = new ZDate(text);
    return isNaN(date) ? text : format(date, isMonth ? "yyyy-MM" : "yyyy-MM-dd");
  };

  const formatCondition = node => {
    const { filterType, operator, values } = node;
    let { headerName } = node;
    if (!headerName || !operator || !values?.length) {
      return "";
    }
    const matchCol = filterColumns.find(col => (col.filterName || col.headerName) === headerName);
    headerName = matchCol ? (matchCol.headerName) : headerName;

    let displayHeaderName = "";
    if (matchCol?.groupLabel) {
      displayHeaderName = `${tSafe(matchCol.groupLabel)} ${matchCol.headerName ?? ""}`.trim();
    } else if (matchCol) {
      displayHeaderName = `${tSafe(matchCol.rootLabel)} ${tSafe(matchCol.label)}`.trim();
    } else if (headerName.includes(".")) {
      const parts = headerName.split(".");
      displayHeaderName = parts.map(part => tSafe(part)).join(" ");
    } else {
      displayHeaderName = tSafe(headerName);
    }

    let valueStr = values.map(v => `'${tSafe(v)}'`);
    const opLabel = getOperatorOptions(filterType).find(op => op === operator) || operator;

    if (filterType === "percent") {
      valueStr = values.map(v => {
        if (v !== null) {
          const num = parseFloat(v);
          return isNaN(num) ? "" : (num * 100).toFixed(2);
        }
        return "";
      });
    } else if (filterType === "number") {
      valueStr = values.map(v => {
        if (v !== null) {
          const num = parseFloat(v);
          return isNaN(num) ? "" : num;
        }
        return "";
      });
    } else if (filterType === "boolean") {
      valueStr = values.map(v => (v === 'Y' ? t("active") : t("inactive")));
    } else if (filterType === "date" || filterType === "timestamp") {
      /*
       * 날짜는 날짜만 보여 준다 — base_date 처럼 원본이 `2026-08-27T10:34:37.0306547Z` 인 값이 있어
       * 그대로 찍으면 요약 한 줄이 타임스탬프로 가득 찬다. 월 단위 필터면 `yyyy-MM` 까지만.
       */
      valueStr = values.map(v => formatDateValue(v, node.filterUnit));
    }

    if (operator === "inRange" && values.length === 2) {
      return `${displayHeaderName} ${tSafe(opLabel)} ${valueStr[0] !== "" && valueStr[1] !== "" ? `${valueStr[0]} ~ ${valueStr[1]}` : ""}`;
    } else if (operator === "notConfigured") {
      return `${displayHeaderName} ${t("notConfigured")}`;
    }

    return `${displayHeaderName} ${tSafe(opLabel)} ${values.length > 1 ? `(${valueStr})` : valueStr}`;
  };


  const stringifyFilter = advancedFilter => {
    if (!advancedFilter) {
      return "";
    }

    if (advancedFilter.children?.length) {
      const logic = advancedFilter.logicOperator || "AND";

      const conditions = advancedFilter.children.filter(child => !child.children);
      const groups = advancedFilter.children.filter(child => child.children);
      const sortedChildren = [...conditions, ...groups];

      // 요약 줄은 읽기 전용 미리보기다 — 조건이 수백 개면 문자열 하나가 수만 자가 되고 조건마다 번역·컬럼 탐색이 돌므로
      // 앞쪽 SUMMARY_MAX_PARTS 개만 만들고 나머지는 만들지 않은 채 개수로 줄인다.
      const parts = [];
      let skipped = 0;
      sortedChildren.forEach(child => {
        if (parts.length >= SUMMARY_MAX_PARTS) {
          skipped += 1;
          return;
        }
        const str = stringifyFilter(child);
        if (str !== "") {
          parts.push(child.children ? `(${str})` : str);
        }
      });

      const text = parts.join(` ${logic} `);
      return skipped > 0 ? `${text} ${logic} … (+${skipped})` : text;
    }

    return formatCondition(advancedFilter);
  };

  /*
   * 요약 칩 — 최상위 자식 하나가 칩 하나다. 그룹이면 통째로 괄호를 씌워 한 칩에 담는다.
   * 문자열을 잘라 만들지 않는 이유는 아래 렌더 주석 참고(괄호 안까지 잘렸다).
   */
  // 번역·컬럼 탐색이 조건마다 돌기 때문에 트리/컬럼이 바뀔 때만 다시 만든다(입력 한 글자마다 수백 개를 다시 만들지 않는다).
  const summaryChips = useMemo(() => {
    const logic = advancedFilter?.logicOperator || "AND";
    const parts = (advancedFilter?.children ?? [])
      .map(child => {
        const text = stringifyFilter(child);
        if (!text) {
          return null;
        }
        return child.children ? `(${text})` : text;
      })
      .filter(Boolean);
    return { logic, parts };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilter, filterColumns]);

  /* 부모가 들고 있는 필터를 패널 상태로 되받는다 — 단, 방금 비운 트리가 그대로 돌아온 것은 무시한다. */
  useEffect(() => {
    const clearedTree = clearedTreeRef.current;
    if (clearedTree && isEqual(normalizeAdvancedFilterTree(advancedFilters), clearedTree)) {
      // 비운 결과가 그대로 올라온 echo — 화면은 이미 비어 있으니 그대로 둔다(초기 조건 재주입 금지).
      return;
    }

    // 실제로 다른 조건이 들어왔다 → 비움 상태는 끝. 이후에는 평소대로 초기 조건을 심는다.
    clearedTreeRef.current = null;
    setAdvancedFilter(buildInitialFilter(advancedFilters, dataMap, lockedFilterNames));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  const handleChangeSelect = event => {
    const targetNm = event?.target?.name;
    const targetVal = event?.target?.value;

    if (targetNm === "custLevel") {
      const cust = custLevels.filter(function (el) {
        return el.id === targetVal;
      })[0] ?? CUSTOMER;
      setCustLevelCd(cust);
      setCustHrchyNum(cust.hrchyNum ?? codeUtil.hrchyNum.HRCHY);
    } else if (targetNm === "itemLevel") {
      const item = itemLevels.filter(function (el) { return el.id === targetVal })[0] ?? ITEM;
      setItemLevelCd(item);
      setItemHrchyNum(item.hrchyNum ?? codeUtil.hrchyNum.HRCHY);
    }
  };

  useEffect(() => {
    if (dataMap?.levels) {
      const result = dataMap.levels;
      const itemConfig = [];
      const custConfig = [];
      let nextItemLevels = [];
      let nextCustLevels = [];

      result.sort((a, b) => {
        // 1차 정렬: hrchyNum 비교
        if (a.hrchyNum < b.hrchyNum) return -1;
        if (a.hrchyNum > b.hrchyNum) return 1;

        // hrchyNum이 같을 경우 2차 정렬: seqNum 비교
        if (a.seqNum < b.seqNum) return -1;
        if (a.seqNum > b.seqNum) return 1;

        return 0;
      });

      for (const row of result) {
        if (row.itemHrchyFlg) {
          itemConfig.push(row);
        } else {
          custConfig.push(row);
        }
      }

      if (itemConfig.length > 0) {
        nextItemLevels = [...itemConfig, ITEM];
      }
      if (custConfig.length > 0) {
        nextCustLevels = [...custConfig, CUSTOMER];
      }

      setItemLevels(nextItemLevels);
      setCustLevels(nextCustLevels);
      setItemLevelCd(prev => getResolvedLevel(advancedFilters?.itemLevelCd ?? prev ?? dataMap?.itemLevelCd ?? reduxViewState?.itemLevelCd ?? ITEM, nextItemLevels, ITEM));
      setCustLevelCd(prev => getResolvedLevel(advancedFilters?.custLevelCd ?? prev ?? dataMap?.custLevelCd ?? reduxViewState?.custLevelCd ?? CUSTOMER, nextCustLevels, CUSTOMER));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataMap?.levels, advancedFilters?.itemLevelCd, advancedFilters?.custLevelCd]);

  return (
    <>
      {/*
       * fontFamily 를 여기서 한 번 준다 — 앱에 전역 CssBaseline 이 없어서
       * body 에 테마 폰트가 깔리지 않는다. MUI Typography 계열은 테마에서 받지만
       * 이 패널은 Box(div) 로 짜여 있어 지정하지 않으면 브라우저 기본 글꼴로 떨어진다.
       */}
      {/*
       * height:100% + flex 컬럼 — 본문만 스크롤하고 푸터(전체 삭제·확인)는 카드 바닥에 남는다.
       * 래퍼에 높이가 없는 화면은 height 가 auto 로 풀려 본문이 그대로 늘어나므로,
       * 그때는 푸터의 sticky 가 폴백으로 동작한다.
       */}
      <Box sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#fff",
        fontFamily: FONT_FAMILY_CSS,
      }}>
        <PanelHeader
          icon={<FilterAltIcon />}
          title={t("advancedFilter")}
          subtitle={[
            `${countConditions(advancedFilter)} ${t("filter")}`,
            dataMap?.levels
              ? [custLevelCd?.name, itemLevelCd?.name].filter(Boolean).join(" / ")
              : null,
          ].filter(Boolean).join(" · ")}
          onClose={onClose}
          closeLabel={t("close")}
        />

        {/*
         * flex-basis 를 auto 로 두는 이유 — 래퍼에 높이가 없으면(basis 0 일 때) 본문이 0 으로
         * 찌그러진다. auto 면 내용 높이에서 출발해, 높이가 정해진 래퍼에서만 줄어들며 스크롤한다.
         * 자식의 flexShrink 를 막는 이유는 SidePanel 본문 주석과 같다(찌그러지고 스크롤바가 안 생긴다).
         */}
        <Box sx={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          p: "15px 17px 17px",
          display: "flex",
          flexDirection: "column",
          gap: "15px",
          "& > *": { flexShrink: 0 },
        }}>

        {/* [B] 데이터 레벨 전용 설정 행 (Appropriate Placement) */}
        {dataMap?.levels ? (
          <Box>
            <Box sx={{ ...PANEL_LABEL_SX, mb: "8px" }}>
              {t("searchLevel")}
            </Box>
            <Box sx={{ display: "flex", gap: "9px" }}>
              {custLevels?.length > 0 ? (
                <FormControl sx={LEVEL_SX}>
                  <InputLabel>{t(CUST_LEVEL)}</InputLabel>
                  <Select
                    id={CUST_LEVEL}
                    name={CUST_LEVEL}
                    value={getSelectValue(custLevelCd, custLevels)}
                    label={t(CUST_LEVEL)}
                    size="small"
                    variant="outlined"
                    onChange={event => {
                      handleChangeSelect(event);
                    }}>
                    {custLevels.flatMap((custLevel, idx) => {
                      const groupCd = custLevel?.grpCd || t("bottom");
                      const prevGroupCd = idx > 0 ? custLevels[idx - 1]?.grpCd : null;
                      const isNewGroup = groupCd !== prevGroupCd;
                      const menuItems = [];

                      if (isNewGroup) {
                        menuItems.push(
                          <ListSubheader
                            key={`cust-group-${idx}-${groupCd}`}
                            disableSticky
                            sx={{
                              p: 1,
                              fontWeight: 700,
                              fontSize: FS.SUB,
                              lineHeight: 1.5,
                              minHeight: 'unset',
                              color: "primary.main",
                              bgcolor: "action.hover",
                              borderTop: idx === 0 ? "none" : "1px solid",
                              borderColor: "divider"
                            }}
                          >
                            {custLevel?.grpName || groupCd}
                          </ListSubheader>
                        );
                      }

                      menuItems.push(
                        <MenuItem key={custLevel.id} value={custLevel.id} sx={{ pl: 1.5 }}>
                          {custLevel.name}
                        </MenuItem>
                      );

                      return menuItems;
                    })}
                  </Select>
                </FormControl>
              ) : null}
              {itemLevels?.length > 0 ? (
                <FormControl sx={LEVEL_SX}>
                  <InputLabel>{t(ITEM_LEVEL)}</InputLabel>
                  <Select
                    id={ITEM_LEVEL}
                    name={ITEM_LEVEL}
                    value={getSelectValue(itemLevelCd, itemLevels)}
                    label={t(ITEM_LEVEL)}
                    size="small"
                    variant="outlined"
                    onChange={event => {
                      handleChangeSelect(event);
                    }}>
                    {itemLevels?.flatMap((itemLevel, idx) => {
                      const groupCd = itemLevel?.grpCd || t("bottom");
                      const prevGroupCd = idx > 0 ? itemLevels[idx - 1]?.grpCd : null;
                      const isNewGroup = groupCd !== prevGroupCd;
                      const menuItems = [];

                      if (isNewGroup) {
                        menuItems.push(
                          <ListSubheader
                            key={`item-group-${idx}-${groupCd}`}
                            disableSticky
                            sx={{
                              p: 1,
                              fontWeight: 700,
                              fontSize: FS.SUB,
                              lineHeight: 1.5,
                              minHeight: 'unset',
                              color: "primary.main",
                              bgcolor: "action.hover",
                              borderTop: idx === 0 ? "none" : "1px solid",
                              borderColor: "divider"
                            }}
                          >
                            {itemLevel?.grpName || groupCd}
                          </ListSubheader>
                        );
                      }

                      menuItems.push(
                        <MenuItem key={itemLevel.id} value={itemLevel.id} sx={{ pl: 1.5 }}>
                          {itemLevel.name}
                        </MenuItem>
                      );

                      return menuItems;
                    })}
                  </Select>
                </FormControl>
              ) : null}
            </Box>
          </Box>
        ) : null }
        {itemTypes.length > 0 ? (
          <Box>
            <Box sx={{ ...PANEL_LABEL_SX, mb: "8px" }}>
              {`${t("search")} ${t("itemType")}`}
            </Box>
            <Autocomplete
              key={"item-type-auto-complete"}
              options={itemTypes}
              getOptionLabel={option => itemType ? t(itemType[option]?.name) : option}
              value={itemTypeSearch}
              disableClearable
              onChange={(_, newValue) => setItemTypeSearch(newValue)}
              isOptionEqualToValue={(option, value) => option === value}
              size="small"
              sx={{ width: "100%" }}
              renderInput={(params) => (
                <TextField {...params} label={t("itemType")} />
              )}
            />
          </Box>
        ) : null }

        {/*
         * 적용 중인 조건 요약 — 시안의 칩 줄.
         *   한 줄짜리 문자열(#F0F0F0 위의 굵은 청록 텍스트)이던 것을 조건마다 칩으로 끊는다.
         *   길어질수록 어디서 조건이 나뉘는지 안 보이던 문제를 없앤다. 칩은 읽기 전용이다 —
         *   지우기는 아래 편집기의 × 로만 한다(잠긴 조건은 지울 수 없으므로 칩에 × 를 달면 거짓말이 된다).
         */}
        {summaryChips.parts.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: "7px", alignItems: "center" }}>
            {summaryChips.parts.map((part, idx) => (
              <Fragment key={`${idx}-${part}`}>
                {/*
                 * 연산자는 칩 **밖에** 둔다. 예전에는 문자열을 `AND` 앞에서 잘라 칩 안에 붙였는데,
                 *   ① 괄호 안의 AND/OR 까지 잘려 그룹이 두 칩으로 쪼개졌고
                 *   ② 칩 안에 든 AND 가 그 조건의 일부처럼 보여 어느 단계의 연결인지 알 수 없었다.
                 * 지금은 트리에서 바로 만든다 — 칩 하나 = 최상위 자식 하나, 그룹은 괄호째 한 칩이다.
                 */}
                {idx > 0 && (
                  <Box
                    component="span"
                    sx={{ fontSize: FS.CAPTION, fontWeight: 600, letterSpacing: "0.06em", color: PANEL.inkMuted }}
                  >
                    {summaryChips.logic}
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex", alignItems: "center",
                    minHeight: 27, px: "11px", py: "3px",
                    borderRadius: "14px",
                    bgcolor: "#e9f6fa",
                    boxShadow: `inset 0 0 0 1px ${PANEL.accentLine}`,
                    fontSize: FS.SUB,
                    color: PANEL.accentDeep,
                    overflowWrap: "anywhere",
                  }}
                >
                  {part}
                </Box>
              </Fragment>
            ))}
          </Box>
        ) : null}

        <FilterGroup
          filterNode={advancedFilter}
          onChange={setAdvancedFilter}
          columnDefs={filterColumns}
          columnMenu={columnMenu}
          getOperatorOptions={getOperatorOptions}
          lockedFilterNames={lockedNames}
        />
        </Box>

        <Box
          sx={{
            flex: "none",
            position: "sticky", bottom: 0, zIndex: 2,
            display: "flex", alignItems: "center", gap: "10px",
            p: "13px 17px",
            borderTop: `1px solid ${PANEL.footLine}`,
            bgcolor: PANEL.footBg,
          }}
        >
          <PanelDangerButton
            icon={<DeleteForeverIcon />}
            onClick={() => {
              const seenLockedNames = new Set();
              const lockedChildren = lockedFilterNames.length > 0
                ? (advancedFilter?.children ?? []).filter(c => {
                    const name = c.headerName ?? c.filterName;
                    if (c.children || !lockedFilterNames.includes(name) || seenLockedNames.has(name)) {
                      return false;
                    }
                    seenLockedNames.add(name);
                    return true;
                  })
                : [];
              const cleared = { logicOperator: "AND", children: lockedChildren };
              // 비움을 기억해 둔다 — 프롭 echo 로 초기 조건이 되살아나지 않게(clearedTreeRef 주석 참고).
              clearedTreeRef.current = cloneDeep(cleared);
              setAdvancedFilter(cleared);
              /*
               * 조회 레벨 / 품목 유형도 기본값으로 되돌린다.
               *   조건만 지우고 레벨을 남기면 ① 패널에는 직전 레벨(예: Country / Bike Category)이 그대로 보이고
               *   ② 아래 onApply 는 레벨이 빠진 트리 형태라서 부모는 레벨이 풀린 줄 알게 되어 화면과 조회가 어긋난다.
               */
              setCustLevelCd(CUSTOMER);
              setItemLevelCd(ITEM);
              setCustHrchyNum(codeUtil.hrchyNum.HRCHY);
              setItemHrchyNum(codeUtil.hrchyNum.HRCHY);
              setItemTypeSearch(DEFAULT_ITEM_TYPE);
              if (onRemoveAll) {
                onRemoveAll(cleared);
              } else {
                onApply?.(cleared);
              }
            }}
          >
            {t("removeAll")}
          </PanelDangerButton>
          <Box sx={{ flex: 1 }} />
          <PanelPrimaryButton icon={<DoneAllIcon />} onClick={handleConfirm}>
            {t("confirm")}
          </PanelPrimaryButton>
        </Box>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo(prev => ({ ...prev, open: false }))}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default AdvancedFilter;